import { ensureCurrentUser, ensureOwnListing } from './data';
import { getUserEventFromCurrentUser, UserEvents } from '../ducks/UserEvents';
import { userField } from './userField';
import { LISTING_STATE_PUBLISHED } from './types';
import { profileHandleError } from './profileHandle';
import { isAlternativePayoutConfigured } from './alternativePayout';

export const CHECKLIST_STEP_ID = {
  VERIFY_EMAIL: 'verify_email',
  COMPLETE_PROFILE: 'complete_profile',
  CREATE_LISTING: 'create_listing',
  ADD_PAYOUT: 'add_payout',
  SHARE_LISTING: 'share_listing',
};

export const CHECKLIST_STEP_IDS = [
  CHECKLIST_STEP_ID.VERIFY_EMAIL,
  CHECKLIST_STEP_ID.COMPLETE_PROFILE,
  CHECKLIST_STEP_ID.CREATE_LISTING,
  CHECKLIST_STEP_ID.ADD_PAYOUT,
  CHECKLIST_STEP_ID.SHARE_LISTING,
];

const hasNonEmptyString = value => typeof value === 'string' && value.trim().length > 0;

const hasNonEmptyArray = value => Array.isArray(value) && value.length > 0;

const hasLocation = location => {
  if (!location) {
    return false;
  }
  if (typeof location === 'string') {
    return hasNonEmptyString(location);
  }
  if (typeof location !== 'object') {
    return false;
  }
  const selectedPlace = location.selectedPlace || location;
  const address = selectedPlace.address || location.address || location.search;
  return hasNonEmptyString(address);
};

/**
 * Returns which profile fields are still missing for checklist step 2.
 * @param {Object} currentUser
 * @returns {string[]} field keys: profileImage | bio | handle | languages | categories | location
 */
export const getMissingProfileFields = currentUser => {
  const user = ensureCurrentUser(currentUser);
  const attrs = user.attributes?.profile || {};
  const publicData = attrs.publicData || {};
  const missing = [];

  if (!user.profileImage?.id) {
    missing.push('profileImage');
  }
  if (!hasNonEmptyString(attrs.bio)) {
    missing.push('bio');
  }
  const handle = publicData.handle;
  if (!handle || profileHandleError(handle)) {
    missing.push('handle');
  }
  if (!hasNonEmptyArray(publicData[userField.LANGUAGES])) {
    missing.push('languages');
  }
  if (!hasNonEmptyArray(publicData[userField.CATEGORIES])) {
    missing.push('categories');
  }
  if (!hasLocation(publicData[userField.LOCATION])) {
    missing.push('location');
  }
  return missing;
};

export const hasPublishedListing = listings =>
  (listings || []).some(listing => {
    const ensured = ensureOwnListing(listing);
    return ensured.attributes?.state === LISTING_STATE_PUBLISHED;
  });

export const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData != null &&
  stripeAccountData.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

export const isPayoutReady = (currentUser, stripeAccount) => {
  if (isAlternativePayoutConfigured(currentUser)) {
    return true;
  }

  const user = ensureCurrentUser(currentUser);
  const account = stripeAccount || user.stripeAccount;
  const stripeConnected = !!(account?.id || user.attributes?.stripeConnected);
  if (!stripeConnected || !account?.id) {
    return false;
  }
  const data = account.attributes?.stripeAccountData;
  if (!data) {
    // currentUser?include=stripeAccount often omits stripeAccountData (that comes from
    // sdk.stripeAccount.fetch). Match EditListingWizard: no evaluable due requirements.
    return true;
  }
  const requirementsMissing =
    hasRequirements(data, 'past_due') || hasRequirements(data, 'currently_due');
  return !requirementsMissing;
};

/**
 * @typedef {Object} ChecklistStep
 * @property {string} id
 * @property {boolean} done
 * @property {boolean} locked
 * @property {string[]} missingFields
 */

/**
 * Compute practitioner onboarding checklist state.
 * @param {Object} params
 * @param {Object} params.currentUser
 * @param {Array} [params.ownListings]
 * @param {Object} [params.stripeAccount]
 * @returns {{ steps: ChecklistStep[], completedCount: number, percent: number, isComplete: boolean }}
 */
export const getPractitionerChecklistState = ({
  currentUser,
  ownListings = [],
  stripeAccount = null,
} = {}) => {
  const user = ensureCurrentUser(currentUser);
  const emailVerified = !!user.attributes?.emailVerified;
  const missingProfileFields = getMissingProfileFields(user);
  const profileDone = missingProfileFields.length === 0;
  const listingDone = hasPublishedListing(ownListings);
  const payoutDone = isPayoutReady(user, stripeAccount);
  const shareDone = !!getUserEventFromCurrentUser(user, UserEvents.USER_EVENT_FIRST_LISTING_SHARED);

  const steps = [
    {
      id: CHECKLIST_STEP_ID.VERIFY_EMAIL,
      done: emailVerified,
      locked: false,
      missingFields: emailVerified ? [] : ['email'],
    },
    {
      id: CHECKLIST_STEP_ID.COMPLETE_PROFILE,
      done: profileDone,
      locked: false,
      missingFields: missingProfileFields,
    },
    {
      id: CHECKLIST_STEP_ID.CREATE_LISTING,
      done: listingDone,
      locked: false,
      missingFields: listingDone ? [] : ['listing'],
    },
    {
      id: CHECKLIST_STEP_ID.ADD_PAYOUT,
      done: payoutDone,
      locked: false,
      missingFields: payoutDone ? [] : ['payout'],
    },
    {
      id: CHECKLIST_STEP_ID.SHARE_LISTING,
      done: shareDone,
      locked: !listingDone,
      missingFields: shareDone ? [] : ['share'],
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const percent = Math.round((completedCount / steps.length) * 100);

  return {
    steps,
    completedCount,
    percent,
    isComplete: completedCount === steps.length,
  };
};
